import { LcaProductFolderOutSchema } from '../types';

export interface FolderTreeRow {
  folder: LcaProductFolderOutSchema;
  depth: number;
  hasChildren: boolean;
}

const byName = (a: LcaProductFolderOutSchema, b: LcaProductFolderOutSchema) =>
  a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  });

// a folder whose parent is not in the list is shown at the root, so it never disappears
const groupByParent = (folders: LcaProductFolderOutSchema[]) => {
  const ids = new Set(folders.map((folder) => folder.id));
  const childrenByParent = new Map<
    number | null,
    LcaProductFolderOutSchema[]
  >();
  folders.forEach((folder) => {
    const parentId =
      folder.parent_id !== null && ids.has(folder.parent_id)
        ? folder.parent_id
        : null;
    childrenByParent.set(parentId, [
      ...(childrenByParent.get(parentId) || []),
      folder,
    ]);
  });
  childrenByParent.forEach((children) => children.sort(byName));
  return childrenByParent;
};

/**
 * Folders in display order: each folder is followed by its sub-folders, sorted
 * by name on every level. Children of a collapsed folder are left out.
 */
export const flattenFolderTree = (
  folders: LcaProductFolderOutSchema[],
  // eslint-disable-next-line no-unused-vars
  isExpanded: (folderId: number) => boolean = () => true
): FolderTreeRow[] => {
  const childrenByParent = groupByParent(folders);
  const rows: FolderTreeRow[] = [];
  const visited = new Set<number>();

  const visit = (parentId: number | null, depth: number) => {
    (childrenByParent.get(parentId) || []).forEach((folder) => {
      if (visited.has(folder.id)) return;
      visited.add(folder.id);
      const hasChildren = !!childrenByParent.get(folder.id)?.length;
      rows.push({ folder, depth, hasChildren });
      if (hasChildren && isExpanded(folder.id)) visit(folder.id, depth + 1);
    });
  };

  visit(null, 0);
  return rows;
};

/** The folder itself and every folder nested below it, at any depth. */
export const getSubtreeIds = (
  folders: LcaProductFolderOutSchema[],
  rootId: number
): Set<number> => {
  const childrenByParent = groupByParent(folders);
  const subtree = new Set<number>();
  const pending = [rootId];
  while (pending.length) {
    const folderId = pending.pop() as number;
    if (!subtree.has(folderId)) {
      subtree.add(folderId);
      (childrenByParent.get(folderId) || []).forEach((child) =>
        pending.push(child.id)
      );
    }
  }
  return subtree;
};

/** Folders from the top level down to `folderId`, inclusive. */
export const getFolderPath = (
  folders: LcaProductFolderOutSchema[],
  folderId: number
): LcaProductFolderOutSchema[] => {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const path: LcaProductFolderOutSchema[] = [];
  let current = byId.get(folderId);
  while (current && !path.includes(current)) {
    path.unshift(current);
    current =
      current.parent_id !== null ? byId.get(current.parent_id) : undefined;
  }
  return path;
};

export const formatFolderPath = (path: LcaProductFolderOutSchema[]) =>
  path.map((folder) => folder.name).join(' / ');

/**
 * Deleting a folder lifts its sub-folders one level up. Names of the ones that
 * would collide with a folder already sitting there — the backend refuses those.
 */
export const getDeleteNameClashes = (
  folders: LcaProductFolderOutSchema[],
  folder: LcaProductFolderOutSchema
): string[] => {
  const levelAbove = new Set(
    folders
      .filter(
        (other) =>
          other.parent_id === folder.parent_id && other.id !== folder.id
      )
      .map((other) => other.name)
  );
  return folders
    .filter(
      (child) => child.parent_id === folder.id && levelAbove.has(child.name)
    )
    .map((child) => child.name)
    .sort();
};
